import I18n from 'I18n';
import { getDisplayedValue } from 'customer-data-property-utils/PropertyValueDisplay';
import { ONE_TO_MANY } from 'automation-ui-associations/constants/AssociationCardinality';
import { SortRecordBy, AssociationTimestampOrder } from 'automation-ui-formula-editor/data-passing-panel/types/dataSourcesTypes';
import getAssociationLabel from 'automation-ui-associations/utils/getAssociationLabel';
import AssociationDefinition from 'automation-ui-associations/records/AssociationDefinition';
import { handleGetObjectTypeById } from 'automation-ui-formula-editor/data-sources/queries/getObjectTypeById';
import associationSupportsCustomLabels from 'automation-ui-associations/utils/associationSupportsCustomLabels';
import { EMPTY_PROPERTY } from 'automation-ui-formula-editor/data-sources/constants/propertyConstants';
import { getObjectTypeName } from 'automation-ui-references/object-types/objectTypeUtils';
import { getFormattedMessageWithObjectTypeName } from 'automation-ui-i18n/i18nUtils';
import { getFilterTargetType } from 'automation-ui-formula-editor/data-passing-panel/utils/getFilterTargetType';
const getSortRecordByNameLabel = sortBy => {
  const {
    property,
    order
  } = sortBy;
  switch (property.toUpperCase()) {
    case SortRecordBy.HS_CREATEDATE:
      if (order === 'DESC') {
        return I18n.text('automation-ui-formula-editor.fetch-object-panel.add-data-source.form-components.one-to-many-drop-down.options-text.recent-created.data-source-label');
      } else {
        return I18n.text('automation-ui-formula-editor.fetch-object-panel.add-data-source.form-components.one-to-many-drop-down.options-text.first-created.data-source-label');
      }
    case SortRecordBy.HS_LASTMODIFIEDDATE:
    default:
      return I18n.text('automation-ui-formula-editor.fetch-object-panel.add-data-source.form-components.one-to-many-drop-down.options-text.recent-updated.data-source-label');
  }
};
const getAssociationTimestampOrderLabel = metadata => {
  switch (metadata.associationTimestampOrder) {
    case AssociationTimestampOrder.MOST_RECENTLY_ASSOCIATED:
      return I18n.text('automation-ui-formula-editor.fetch-object-panel.add-data-source.form-components.one-to-many-drop-down.options-text.recent-associated.data-source-label');
    case AssociationTimestampOrder.FIRST_ASSOCIATED:
      return I18n.text('automation-ui-formula-editor.fetch-object-panel.add-data-source.form-components.one-to-many-drop-down.options-text.first-associated.data-source-label');
    default:
      return '';
  }
};
const makeAssociationTimestampFilterLabel = async (dataFromAPI, metadata, {
  resolveObjectTypeById
}) => {
  const {
    associationDefinition
  } = metadata;
  const {
    fromObjectTypeId,
    toObjectTypeId,
    cardinality,
    hasAllAssociatedObjects
  } = associationDefinition;
  const objectTypeDefinition = await resolveObjectTypeById(toObjectTypeId);
  const isMultiObjectDataSource = getFilterTargetType(metadata) === 'MANY';
  const objectName = getObjectTypeName({
    objectTypeDefinition,
    isPlural: isMultiObjectDataSource,
    isCapitalized: true
  });
  let orderLabel;
  if ('associationTimestampOrder' in metadata) {
    orderLabel = getAssociationTimestampOrderLabel(metadata);
  } else if (metadata.sortBy) {
    orderLabel = getSortRecordByNameLabel(metadata.sortBy);
  } else {
    orderLabel = I18n.text('automation-ui-formula-editor.fetch-object-panel.add-data-source.form-components.one-to-many-drop-down.options-text.all.data-source-label');
  }
  if (hasAllAssociatedObjects) {
    return `${objectName}: ${orderLabel}`;
  } else {
    const toObjectTypeDefinition = await handleGetObjectTypeById(toObjectTypeId);
    const associationName = getAssociationLabel({
      associationDefinition: new AssociationDefinition(associationDefinition),
      toObjectTypeDefinition
    });
    const areCustomLabelsSupported = associationSupportsCustomLabels(fromObjectTypeId || '', toObjectTypeId || '');
    if (!areCustomLabelsSupported && cardinality !== ONE_TO_MANY) {
      return objectName;
    }
    if (!areCustomLabelsSupported && cardinality === ONE_TO_MANY) {
      return `${objectName}: ${orderLabel}`;
    }
    if (associationName && associationName !== 'UNKNOWNS' && cardinality === ONE_TO_MANY) {
      return `${objectName}: ${associationName}, ${orderLabel}`;
    }
    if (associationName && associationName !== 'UNKNOWNS' && cardinality !== ONE_TO_MANY) {
      return `${objectName}: ${associationName}`;
    }
    if ((!associationName || associationName === 'UNKNOWNS') && cardinality === ONE_TO_MANY) {
      return `${objectName}: ${orderLabel}`;
    }
    return objectName;
  }
};
const makeMatchingPropertyFilterLabel = async (dataFromAPI, metadata, {
  resolveObjectTypeById,
  resolvePropertyOptionLabel
}) => {
  var _dataFromAPI$dataSour;
  const objectTypeDefinition = await resolveObjectTypeById(metadata.objectTypeId);
  const isMultiObjectDataSource = getFilterTargetType(metadata) === 'MANY';
  const objectName = getObjectTypeName({
    objectTypeDefinition,
    isPlural: isMultiObjectDataSource,
    isCapitalized: true
  });
  const {
    recordFilterDefinition
  } = metadata;
  const {
    matchingValue
  } = recordFilterDefinition;
  const targetPropertyName = recordFilterDefinition.propertyName;
  const targetProperty = (_dataFromAPI$dataSour = dataFromAPI.dataSourceProperties[targetPropertyName].metadata) === null || _dataFromAPI$dataSour === void 0 ? void 0 : _dataFromAPI$dataSour.typeDefinition;
  if (!targetProperty) {
    return objectName;
  }
  if (matchingValue.type === 'STATIC') {
    let formattedValue = null;
    if (targetProperty.type === 'enumeration') {
      try {
        formattedValue = await resolvePropertyOptionLabel({
          objectTypeId: metadata.objectTypeId,
          propertyName: targetPropertyName,
          value: matchingValue.value
        });
      } catch (_unused) {
        formattedValue = null;
      }
    } else {
      formattedValue = getDisplayedValue(Object.assign({}, EMPTY_PROPERTY, targetProperty), matchingValue.value, undefined, undefined, metadata.objectTypeId);
    }
    if (formattedValue) {
      const matchingPropertyValueLabel = I18n.text('automation-ui-formula-editor.data-dictionary.data-source-label.matching-property.property-value', {
        propertyName: targetProperty.label,
        propertyValue: formattedValue
      });
      return `${objectName}: ${matchingPropertyValueLabel}`;
    } else {
      const matchingPropertyValueLabel = I18n.text('automation-ui-formula-editor.data-dictionary.data-source-label.matching-property.property-value-missing', {
        propertyName: targetProperty.label
      });
      return `${objectName}: ${matchingPropertyValueLabel}`;
    }
  } else {
    const matchingPropertyLabel = I18n.text('automation-ui-formula-editor.data-dictionary.data-source-label.matching-property.enrolled-property', {
      propertyName: targetProperty.label
    });
    return `${objectName}: ${matchingPropertyLabel}`;
  }
};
export const makeFetchedObjectI18nLabel = async (dataFromAPI, mutators) => {
  const metadata = dataFromAPI.dataSourceDefinition.metadata;
  if ('associationDefinition' in metadata) {
    return await makeAssociationTimestampFilterLabel(dataFromAPI, metadata, mutators);
  } else if ('recordFilterDefinition' in metadata) {
    return await makeMatchingPropertyFilterLabel(dataFromAPI, metadata, mutators);
  } else {
    return '';
  }
};
export const makeEnrolledObjectI18nLabel = async (dataFromAPI, {
  resolveObjectTypeById
}) => {
  const metadata = dataFromAPI.dataSourceDefinition.metadata;
  const {
    objectTypeId
  } = metadata;
  const objectTypeDefinition = await resolveObjectTypeById(objectTypeId);
  return getFormattedMessageWithObjectTypeName({
    baseLangKey: 'automation-ui-formula-editor.data-dictionary.data-source-label.enrolled-object_cob',
    objectTypeId,
    objectTypeDefinition
  });
};